import { CheckCircle2, XCircle } from "lucide-react";
import { useI18n } from "@/hooks/useI18n";
import type { FunctionStats } from "@/lib/admin/hooks/useEdgeFunctionStats";

interface EdgeFunctionTableProps {
  functions: FunctionStats[];
  periodDays: number;
}

export function EdgeFunctionTable({ functions, periodDays }: EdgeFunctionTableProps) {
  const { t } = useI18n();

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        {t("adminDashboard.edgeFunctions.periodLabel", { days: String(periodDays) })}
      </p>
      <div className="overflow-x-auto rounded-md border border-gray-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <th className="py-2 px-3 text-left">
                {t("adminDashboard.edgeFunctions.function")}
              </th>
              <th className="py-2 px-3 text-right">
                {t("adminDashboard.edgeFunctions.totalCalls")}
              </th>
              <th className="py-2 px-3 text-right">
                {t("adminDashboard.edgeFunctions.successRate")}
              </th>
              <th className="py-2 px-3 text-right">
                {t("adminDashboard.edgeFunctions.avgMs")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {functions.map((fn) => {
              const successRate =
                fn.total_calls > 0
                  ? Math.round((fn.success_calls / fn.total_calls) * 1000) / 10
                  : 0;
              const isHealthy = successRate >= 80;
              const successColor = isHealthy ? "text-green-600" : "text-red-600";

              return (
                <tr key={fn.slug} className="hover:bg-gray-50 transition-colors">
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      {isHealthy ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                      )}
                      <span className="font-mono text-gray-800">{fn.slug}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right font-semibold text-gray-900">
                    {fn.total_calls.toLocaleString()}
                  </td>
                  <td className={`py-2 px-3 text-right font-semibold ${successColor}`}>
                    {successRate.toFixed(1)}%
                  </td>
                  <td className="py-2 px-3 text-right text-gray-600">
                    {fn.avg_execution_ms != null
                      ? `${fn.avg_execution_ms.toFixed(0)} ms`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default EdgeFunctionTable;
